import { useSessionLanguage } from '@context/SessionSettingsContext'

import { getCurrencySymbol } from '@utils/transaction.utils'

import { CurrencyType } from '@model/WalletDto'

import { Locale } from '@global/locale/Locale'

export interface FormatMoney {
  (
    number?: number,
    currency?: CurrencyType | string,
    decimalPlaces?: number,
    isCrypto?: boolean
  ): string
}

export interface FormatNumber {
  (
    number?: number | null,
    formatParams?: { minDecimalPlaces?: number; maxDecimalPlaces?: number }
  ): string
}

interface UseFormatNumber {
  formatMoney: FormatMoney
  compactNumber: (num: number, zerosAllowed?: number, stripNumeralSuffix?: boolean) => string
  formatNumber: FormatNumber
}

export const useFormatNumber = (): UseFormatNumber => {
  const locale = useSessionLanguage()

  const formatMoney = (
    number = 0,
    currency: CurrencyType | string = 'USD',
    decimalPlaces = 2,
    isCrypto = false
  ): string => {
    if (isCrypto) {
      const symbol = getCurrencySymbol(currency as CurrencyType)
      const sign = number < 0 ? '-' : ''
      const absoluteNumber = Math.abs(number)

      return `${sign}${symbol}${absoluteNumber}`
    }

    let formatted: string
    try {
      formatted = new Intl.NumberFormat(commonLocaleMapping[locale], {
        style: 'currency',
        currency,
        minimumFractionDigits: Math.min(2, decimalPlaces),
        maximumFractionDigits: decimalPlaces,
      }).format(number ?? 0)
    } catch (error: unknown) {
      console.error(error)
      formatted = ''
    }

    if (formatted.includes(currency)) {
      const symbol = getCurrencySymbol(currency as CurrencyType)
      const absoluteNumber = Math.abs(number)
      formatted = (number < 0 ? `-${symbol}` : symbol) + absoluteNumber.toFixed(decimalPlaces)
    }

    return formatted
  }

  const formatNumber = (
    number: number | null = 0,
    formatParams?: { minDecimalPlaces?: number; maxDecimalPlaces?: number }
  ): string => {
    const { minDecimalPlaces = 2, maxDecimalPlaces = 2 } = formatParams || {}
    return (number ?? 0).toLocaleString(locale, {
      minimumFractionDigits: minDecimalPlaces,
      maximumFractionDigits: maxDecimalPlaces,
    })
  }

  const compactNumber = (num = 0, digits = 1, stripNumeralSuffix = false) => {
    const thersholds = [
      0, 10, 100, 1_000, 10_000, 100_000, 1_000_000, 10_000_000, 100_000_000, 1_000_000_000,
      10_000_000_000, 100_000_000_000,
    ]

    const threshold = thersholds[digits - 1]

    if (threshold > 1 && num >= threshold) {
      const formatted = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
      }).format(num)

      return stripNumeralSuffix ? formatted.replace(/[A-Za-z]+$/, '') : formatted
    }

    return num.toLocaleString('en-US')
  }

  return {
    formatMoney,
    compactNumber,
    formatNumber,
  }
}

export type LocaleMapping = {
  [key in Locale]: string
}

export const commonLocaleMapping: LocaleMapping = {
  ar: 'ar',
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  id: 'id-ID',
  it: 'it-IT',
  ko: 'ko-KR',
  ms: 'ms-MY',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ru: 'ru-RU',
  th: 'th-TH',
  tr: 'tr-TR',
  vi: 'vi-VN',
  zh: 'zh-CN',
  tw: 'zh-TW',
  hk: 'zh-HK',
  ja: 'ja-JP',
}
